import React, {useEffect, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Modal} from "../../../../components/modal/modal.jsx";
import Button from "../../../../components/button/button.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import Loader from "../../../../components/template/loader.jsx";
import DispatchService from "../../../../services/dispatch/dispatch-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import {DASHBOARD_QUERY_KEYS} from "../utils/dashboard-query-keys.js";
import {useDashboardContext} from "../context/dashboard-context.js";
import useDashboardModalState from "../hooks/use-dashboard-modal-state.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";

const DESPACHO_WEB_URL = import.meta.env.VITE_API_DESPACHO_WEB;

const AssignmentDispatchModal = ({payload, onClose, onSuccess}) => {
    const navigate = navigateHook();
    const {configuration} = useDashboardContext();
    const [mileage, setMileage] = useState("");
    const [mileageWarning, setMileageWarning] = useState(null);
    const [saveInvalidMileage, setSaveInvalidMileage] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const requested = Boolean(payload);
    const assignment = payload?.assignment;
    const garrisonId = assignment?.garrisonId;

    const dispatchQuery = useQuery({
        queryKey: DASHBOARD_QUERY_KEYS.garrisonBySetDispatch(garrisonId),
        queryFn: () => DispatchService.getGarrisonBySetDispatch(garrisonId),
        enabled: requested && Boolean(garrisonId),
    });
    const isReady = !dispatchQuery.isFetching && (dispatchQuery.isSuccess || dispatchQuery.isError);
    const {open, closeModal, isPreparing} = useDashboardModalState({payload, isReady, onClose});

    useEffect(() => {
        if (!requested) return;

        setMileage("");
        setMileageWarning(null);
        setSaveInvalidMileage(false);
        setIsSubmitting(false);
    }, [requested]);

    const result = dispatchQuery.data ?? {};
    const garrison = result.garrison;
    const kmControl = Boolean(garrison?.kmControl);
    const actualKm = garrison?.actualKm;

    useEffect(() => {
        if (!requested || !configuration.isSuggestKm || !kmControl || actualKm === undefined || actualKm === null) return;
        setMileage(String(actualKm));
    }, [actualKm, configuration.isSuggestKm, kmControl, requested]);

    const validateMileage = async () => {
        if (!kmControl || mileage === "") return true;

        const mileageResult = await DispatchService.checkMileage(garrisonId, mileage);
        if (!mileageResult?.invalid || saveInvalidMileage) return true;

        setMileageWarning({
            actualKm: mileageResult.actualKm === "" || mileageResult.actualKm === null || mileageResult.actualKm === undefined ? "0" : mileageResult.actualKm,
        });
        return false;
    };

    const handleSubmit = async () => {
        if (!assignment?.id) {
            projectUtils.swalErrorMessage("Erro", "Não foi possível identificar a atribuição.", "Ok");
            return;
        }

        setIsSubmitting(true);

        try {
            const mileageIsValid = await validateMileage();
            if (!mileageIsValid) {
                setIsSubmitting(false);
                return;
            }
            const response = await DispatchService.dispatchGenerateByAssignment({
                assignmentId: assignment.id,
                dispatchToCloseId: null,
                actualKm: mileage,
                finalObservation: null,
            });

            if (!response) {
                projectUtils.swalErrorMessage("Erro", "Não foi possível despachar o item atribuído.", "Ok");
                setIsSubmitting(false);
                return;
            }

            const checkbook = response.numberCheckbook ? ` Talão ${response.numberCheckbook}.` : "";
            onSuccess();
            projectUtils.swalSuccessMessage(
                "success",
                "Sucesso",
                `Item atribuído despachado com sucesso.${checkbook}`,
                "Ok",
                true,
                "Editar",
                ({isDismissed}) => {
                    if (isDismissed) {
                        navigate(`${DESPACHO_WEB_URL}/dispatch/form/${response.id}?redir=despacho/dashboard`, true);
                    }
                },
                false,
                false,
                "#fff",
                {cancelButton: "swal-edit-button"},
            );
        } catch (error) {
            console.error("Erro ao despachar item atribuído pelo dashboard:", error);
            const message = error.response?.data ?? "Não foi possível despachar o item atribuído.";
            projectUtils.swalErrorMessage("Erro", message, "Ok");
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {isPreparing && <Loader/>}
            <Modal open={open} setOpen={closeModal} title="Despachar item atribuído" className="w-full max-w-2xl" isCloseClick={false} animated={false}>
            <Modal.Body className="relative max-h-[calc(100vh-180px)] flex flex-col gap-4 overflow-auto">
                {dispatchQuery.isError && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        Não foi possível carregar os dados da guarnição.
                    </div>
                )}

                {!dispatchQuery.isError && (
                    <>
                        <div>
                            <p className="text-[14px] font-bold text-[#676a6c]">Guarnição</p>
                            <span className="text-sm text-[#555]">{garrison?.name ?? assignment?.garrisonName}</span>
                        </div>

                        <div className="flex flex-col">
                            <p className="text-[14px] font-bold text-[#676a6c]">Item atribuído</p>
                            <span className="text-sm text-[#555]">{assignment?.assignmentSourceText ?? assignment?.assignmentSource}</span>
                            <span className="text-sm text-[#555]">{assignment?.assignmentDescription ?? ""}</span>
                            {assignment?.reasonText ? <span className="text-sm text-[#555]">{assignment.reasonText}</span> : null}
                        </div>

                        {kmControl && (
                            <InputForm
                                id="dashboard-assignment-mileage"
                                label="Km do veículo"
                                integerOnly
                                type="number"
                                value={mileage}
                                min={1}
                                onChange={({target}) => {
                                    setMileage(target.value);
                                    setMileageWarning(null);
                                    setSaveInvalidMileage(false);
                                }}
                            />
                        )}

                        {mileageWarning && (
                            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                <div className="mb-2 text-center">
                                    O km informado é menor que o atual. Deseja continuar?<br/>
                                    Km atual: <b>{mileageWarning.actualKm}</b>
                                </div>
                                <InputCheckbox
                                    id="dashboard-assignment-save-invalid-mileage"
                                    label="Sim, continuar mesmo assim"
                                    checked={saveInvalidMileage}
                                    onChange={({target}) => setSaveInvalidMileage(target.checked)}
                                />
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button type="button" onClick={handleSubmit} loading={isSubmitting} disabled={dispatchQuery.isError || dispatchQuery.isLoading}>Salvar</Button>
            </Modal.Footer>
            </Modal>
        </>
    );
};

export default AssignmentDispatchModal;
